/**
 * car-skins.ts - Shell (car skin) registry.
 *
 * Pure module on purpose: no three, no Rapier, no DOM. The UI stores the initial skin list
 * from here before the game chunk loads, the loader (`car-model.ts`) classifies a glb by the
 * manifest's part names, and `three-game.ts` validates a switch through `skinManifest()`.
 *
 * The glb node names are the contract: `car-model.ts` matches them against `parts` after
 * `normalizeNodeName()` (dots stripped). Names not listed in any bucket end up in the body,
 * which is where the chassis convex hull comes from.
 */

import type { SkinId } from './types.js';

/** Parts the loader splits a glb into; everything unlisted is body geometry. */
export type PartKey =
    | 'frontWheels'
    | 'rearWheels'
    | 'steeringWheel'
    | 'gasPedal'
    | 'brakePedal'
    | 'shifter'
    | 'headlights'
    | 'taillights'
    | 'glass';

/** Where on the body surface a generated lamp patch lives (all fractions of the body box). */
export interface LampPatchRegion {
    /** Vertical band [min, max] between the box bottom (0) and its top (1). */
    height: [number, number];
    /** Depth band measured from the outermost body face inward; fraction of body length. */
    band?: number;
    /** Distance from the centre line |x|, as fractions of the body half width. */
    xHalf?: [number, number];
    /** Outward offset along Z in baked units, so the patch does not z-fight the body. */
    push?: number;
}

/**
 * A cabin-window slab in baked body-box fractions. Used when a shell has no glass meshes and
 * some window faces are not UV-mapped onto the palette glass cell (Model 3 windshield).
 */
export interface GlassPatchRegion {
    /** Vertical band [min, max] between the box bottom (0) and its top (1). */
    height: [number, number];
    /** Length band [min, max] between the box rear (0) and its front (1). */
    length: [number, number];
    /** Distance from the centre line |x|, as fractions of the body half width. */
    xHalf?: [number, number];
}

export interface SkinManifest {
    id: SkinId;
    /** UI label, surfaced on `GameHost.skins` through `skinSummaries()`. */
    label: string;
    /** glb node names per bucket, normalized (no dots), e.g. `Cylinder023`. */
    parts: Partial<Record<PartKey, readonly string[]>>;
    /**
     * The Anglia steering hub bundles the spinning hub + spokes with a fixed arm reaching
     * the dashboard: `car-model.ts` splits that node by axial distance from the ring plane.
     */
    steeringColumnNode?: string;
    columnSplitAxial?: number;
    /**
     * Interior fill: when the glb has no steering wheel / pedals / shifter meshes at all,
     * those buckets come from the donor skin (already baked into car-frame coordinates).
     */
    interiorDonor?: SkinId;
    /** Shift for donor interior parts, because the donor rig matches its own cabin only. */
    interiorOffset?: { x: number; y: number; z: number };
    /**
     * A lamp is cut from the shell's own surface triangles instead of floating quad boxes:
     * the patch keeps the body silhouette, so a shell without lamp meshes still gets
     * flush-mounted glow surfaces. Region values are fractions of the baked body box, and
     * the cut triangles are clipped to the region so no corner can overshoot the band.
     */
    lampPatch?: {
        front?: LampPatchRegion;
        rear?: LampPatchRegion;
    };
    /** Body clearance: how far the shell is lifted above the axle line after baking. */
    bodyDrop?: number;
    /**
     * The shell has no glass meshes; window faces are UV-mapped onto the palette glass cell
     * (prepare-model3-shell.py) and/or listed in `glassPatch`. The loader lifts those triangles
     * into `glassGeometry` and leaves holes in the opaque body so `addGlass` can show the stage
     * from the cockpit. Hull points stay on the unsplit body for the deterministic gate.
     */
    shellTextureAlpha?: boolean;
    /**
     * Extra window slabs (body-box fractions) for faces whose UV misses the palette glass cell.
     * Measured with scripts/blender/analyze-glass-regions.py.
     */
    glassPatch?: readonly GlassPatchRegion[];
    /**
     * Driver's-eye offset from the steering-wheel pivot, in the body frame (+X left, +Y up,
     * +Z forward). Defaults match the Anglia cabin; shells that borrow that wheel must set
     * their own offsets or the camera sits in the roof and the windshield reads as a black wall.
     */
    cockpitEyeOffset?: { x: number; y: number; z: number };
    /** Look-at offset from the eye (same frame as `cockpitEyeOffset`). */
    cockpitLookOffset?: { x: number; y: number; z: number };
    /** Headlight/taillight meshes fall back to lamp quads generated from the body box. */
    generateLamps?: boolean;
}

export const CAR_SKINS: readonly SkinManifest[] = [
    {
        id: 'anglia',
        label: 'Ford Anglia',
        parts: {
            frontWheels: ['Cylinder.023'],
            rearWheels: ['Cylinder.024'],
            steeringWheel: ['Torus.002', 'Cube.074', 'Cube.075'],
            gasPedal: ['Cube.067', 'Cube.068'],
            brakePedal: ['Cube.065', 'Cube.066'],
            shifter: ['Cylinder.030'],
            headlights: ['Plane.021'],
            taillights: ['Plane.041', 'Plane.042', 'Plane.043'],
            glass: ['Plane.001', 'Plane.029', 'Plane.023']
        },
        steeringColumnNode: 'Cube.075',
        columnSplitAxial: 0.04,
        bodyDrop: 1.8
    },
    {
        id: 'tesla-model3',
        label: '特斯拉 Model 3',
        // The Sketchfab shell ships body + four wheels + plate only; its interior is donated
        // by the Anglia rig and its lamps are generated from the body box.
        parts: {
            frontWheels: ['WheelFront'],
            rearWheels: ['WheelRear']
        },
        interiorDonor: 'anglia',
        // Donated wheel a bit forward of mid-cabin so the eye can sit behind it and
        // still clear the opaque roof into the windshield opening.
        interiorOffset: { x: 0, y: -1.5, z: 2.2 },
        lampPatch: {
            front: { height: [0.32, 0.56], band: 0.1, xHalf: [0.52, 0.84], push: 0.12 },
            rear: { height: [0.54, 0.68], band: 0.1, xHalf: [0.38, 0.8], push: 0.12 }
        },
        generateLamps: true,
        // Keep the painted shell in one opaque mesh. Splitting window triangles into a
        // translucent glass mesh punched holes through the roof, trunk and doors.
        // Under the opaque roof, behind the rim, aimed at the windshield opening.
        cockpitEyeOffset: { x: -1.0, y: 0.25, z: -2.8 },
        cockpitLookOffset: { x: 0, y: 0.0, z: 12 }
    },
    // Same contract as Model 3: opaque body, WheelFront / WheelRear, Anglia interior,
    // generated lamps, no glass cut.
    opaqueShell('xiaomi-su7', '小米 SU7')
];

/** Opaque painted shell that borrows the Anglia cockpit and generates its lamps. */
function opaqueShell(id: SkinId, label: string): SkinManifest {
    return {
        id,
        label,
        parts: {
            frontWheels: ['WheelFront'],
            rearWheels: ['WheelRear']
        },
        interiorDonor: 'anglia',
        interiorOffset: { x: 0, y: -1.5, z: 2.2 },
        lampPatch: {
            front: { height: [0.32, 0.56], band: 0.1, xHalf: [0.52, 0.84], push: 0.12 },
            rear: { height: [0.54, 0.68], band: 0.1, xHalf: [0.38, 0.8], push: 0.12 }
        },
        generateLamps: true,
        cockpitEyeOffset: { x: -1.0, y: 0.25, z: -2.8 },
        cockpitLookOffset: { x: 0, y: 0.0, z: 12 }
    };
}

export const DEFAULT_SKIN: SkinId = 'anglia';

export function skinManifest(id: SkinId): SkinManifest {
    const manifest = CAR_SKINS.find(candidate => candidate.id === id);
    if (manifest === undefined) {
        throw new Error(`Unknown car skin: ${id}`);
    }
    return manifest;
}

/** The list the UI renders; written to `GameHost.skins` when the game starts. */
export function skinSummaries(): Array<{ id: SkinId; label: string }> {
    return CAR_SKINS.map(({ id, label }) => ({ id, label }));
}
